import type { Schemas } from "@acervo/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { RefreshCw, UploadCloud } from "lucide-react";
import { Empty, ErrorBox, PageHeader, Spinner, VisibilityBadge } from "../../components/ui";
import { api } from "../../lib/api";
import { dateTime, duration } from "../../lib/format";

export function AdminCoursesPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["admin", "courses"],
    queryFn: () => api.unwrap(api.client.GET("/v1/courses")),
  });
  const imp = useMutation({
    mutationFn: () => api.unwrap(api.client.POST("/v1/admin/media/import")),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "courses"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cursos"
        subtitle="As aulas enviadas pelo acervo-ingest entram sozinhas a cada 10 minutos; o botão importa agora."
        actions={
          <>
          <Link to="/console/envios" className="btn-ghost">
            <UploadCloud className="h-4 w-4" /> Enviar aulas
          </Link>
          <button className="btn-primary" disabled={imp.isPending} onClick={() => imp.mutate()}>
            <RefreshCw className={`h-4 w-4 ${imp.isPending ? "animate-spin" : ""}`} />
            {imp.isPending ? "Importando…" : "Importar do acervo-ingest"}
          </button>
          </>
        }
      />
      {imp.isError && <ErrorBox error={imp.error} />}
      {imp.isSuccess && <ImportSummary r={imp.data} />}
      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <Empty title="Nenhum curso importado">
          Rode <code>acervo-ingest enviar</code> no computador com o HD e depois importe aqui.
        </Empty>
      ) : (
        <CourseTable items={list.data.items} />
      )}
    </div>
  );
}

type ImportResult = { scanned: number; imported: number; updated: number; unchanged: number; incomplete: number; failed: string[] };

function ImportSummary({ r }: { r: ImportResult }) {
  return (
    <div className="card space-y-1 p-4 text-sm">
      <p>
        <strong>{r.imported}</strong> novas · <strong>{r.updated}</strong> atualizadas · {r.unchanged} sem mudança ·{" "}
        {r.scanned} verificadas
      </p>
      {r.incomplete > 0 && <p className="text-muted">{r.incomplete} ainda em envio (sem manifesto); entram na próxima.</p>}
      {r.failed.length > 0 && (
        <details className="text-danger">
          <summary className="cursor-pointer">{r.failed.length} com erro</summary>
          <ul className="mt-1 list-disc pl-5 font-mono text-xs">
            {r.failed.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function CourseTable({ items }: { items: Schemas["CourseCard"][] }) {
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted uppercase">
          <tr className="border-b border-line">
            <th className="px-4 py-2 font-medium">Curso</th>
            <th className="px-4 py-2 font-medium">Aulas</th>
            <th className="px-4 py-2 font-medium">Duração</th>
            <th className="px-4 py-2 font-medium">Acesso</th>
            <th className="px-4 py-2 font-medium">Atualizado</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-line/60 last:border-0 hover:bg-raised">
              <td className="px-4 py-2">
                <Link to="/cursos/$slug" params={{ slug: c.slug }} className="font-semibold hover:text-accent">
                  {c.title}
                </Link>
                <span className="block font-mono text-xs text-muted">{c.slug}</span>
              </td>
              <td className="px-4 py-2">{c.lessons}</td>
              <td className="px-4 py-2">{duration(c.duration_s)}</td>
              <td className="px-4 py-2">
                <VisibilityBadge course={c} />
              </td>
              <td className="px-4 py-2 text-muted">{dateTime(c.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="border-t border-line px-4 py-2 text-xs text-muted">
        Para publicar, editar o título ou marcar amostras grátis, abra o curso.
      </p>
    </div>
  );
}
